import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

// reusable components
import { TableRow, TableCell } from "@/components/ui/table";

// utils and helpers
import { searchFilterByFields } from "../utils/searchFilterExpenses";
import { formatDateTime } from "@/utils/formatDateTime";
import { formatCurrency } from "@/utils/formatCurrency";
import { fetchRecurringExpenses } from "@/services/financeApi";
import { useBusiness } from "@hooks/useBusiness";

export default function useRecurringTable(searchTerm = "") {
  const { businessId } = useBusiness();

  const {
    data: recurring = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    // No recurring-expense data exists in Firestore - empty by design.
    queryKey: ["finance-recurring", businessId],
    queryFn: () => fetchRecurringExpenses(businessId),
    enabled: Boolean(businessId),
    staleTime: 1000 * 60 * 5,
  });

  const filteredRecurring = useMemo(
    () => searchFilterByFields(recurring, searchTerm, ["name", "category", "supplier"]),
    [recurring, searchTerm]
  );

  const recurringColumns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span className="font-medium text-[#1a1a1a]">{row.original.name}</span>,
      },
      {
        accessorKey: "category",
        header: "Category",
        cell: ({ row }) => row.original.category || "-",
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: ({ row }) => formatCurrency(row.original.amount),
      },
      {
        accessorKey: "interval",
        header: "Interval",
        cell: ({ row }) => (
          <span className="capitalize text-[#555]">{row.original.interval || "-"}</span>
        ),
      },
      {
        accessorKey: "nextGenerationDate",
        header: "Next Run",
        cell: ({ row }) => formatDateTime(row.original.nextGenerationDate),
      },
      {
        accessorKey: "autoGenerate",
        header: "Auto",
        cell: ({ row }) =>
          row.original.autoGenerate ? (
            <span className="inline-block rounded-md bg-[#DCFCE7] px-2 py-1 text-xs font-medium text-[#166534]">
              On
            </span>
          ) : (
            <span className="inline-block rounded-md bg-[#e7e7e7] px-2 py-1 text-xs font-medium text-[#333]">
              Off
            </span>
          ),
      },
    ],
    []
  );

  const renderRecurringRow = useCallback((row) => {
    return (
      <TableRow key={row.id} className="hover:bg-[#f6f6ff] transition-colors">
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} className="px-4 py-3 text-[13px] font-medium">
            {cell.getContext().column.columnDef.cell?.(cell.getContext()) ?? cell.renderCell()}
          </TableCell>
        ))}
      </TableRow>
    );
  }, []);

  return {
    allRecurring: recurring,
    recurring: filteredRecurring,
    recurringColumns,
    renderRecurringRow,
    refetch,
    isLoading,
    isError,
  };
}
